/**
 * Firebase Analytics (web) — Google Analytics 4 under the hood. Data flows to
 * both the Firebase console and the linked GA4 property. This is the low-level
 * SDK wrapper; the typed event taxonomy lives in `analytics-events.ts`.
 *
 * Design: never let analytics break app boot. Init is async (GA4 `isSupported()`
 * gates on browser capability), so every helper awaits a single readiness
 * promise; calls made before init resolve once analytics is ready (or no-op if
 * disabled). Disabled = no measurement id, unsupported browser, or local dev
 * (mirrors Sentry — no developer noise in the GA4 property).
 */

import {
  initializeAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics';
import { getFirebaseApp } from './firebase-app';

let analytics: Analytics | null = null;
let ready: Promise<Analytics | null> | null = null;

/**
 * Initialize Firebase Analytics once. Idempotent — returns the same readiness
 * promise on repeat calls. Resolves null when analytics is disabled/unsupported.
 * The measurement id is resolved by Firebase from the backend GA4 link (not the
 * local config) — see `firebase-app.ts`. Safe to call at boot next to `initSentry()`.
 */
export function initAnalytics(): Promise<Analytics | null> {
  if (ready) return ready;
  ready = (async () => {
    const app = getFirebaseApp();
    if (!app) return null;
    try {
      if (!(await isSupported())) return null;
      // Disable the automatic page_view: our SPA strips the ?utm_* query on the
      // first redirect, so the auto page_view would report a clean URL and GA4
      // would attribute the session to "direct". We fire the initial page_view
      // ourselves with the captured landing URL (utm intact) — see analytics-page-view.
      analytics = initializeAnalytics(app, { config: { send_page_view: false } });
      return analytics;
    } catch {
      return null; // analytics must never crash the app
    }
  })();
  return ready;
}

/** Log a GA4 event. No-op when analytics is disabled. */
export async function logAnalyticsEvent(
  name: string,
  params?: Record<string, unknown>,
): Promise<void> {
  const a = await (ready ?? initAnalytics());
  if (!a) return;
  try {
    logEvent(a, name, params);
  } catch {
    // swallow — a failed beacon must not surface to the user
  }
}

/**
 * Identify the signed-in user (GA4 user_id + user properties). Pass null on
 * logout to clear the id. Never send PII (email/name) — ids and coarse props only.
 */
export async function setAnalyticsUser(
  userId: string | null,
  properties?: Record<string, string>,
): Promise<void> {
  const a = await (ready ?? initAnalytics());
  if (!a) return;
  try {
    setUserId(a, userId);
    if (properties) setUserProperties(a, properties);
  } catch {
    // swallow
  }
}

/** Set GA4 user properties without touching the user id. */
export async function setAnalyticsUserProperties(
  properties: Record<string, string>,
): Promise<void> {
  const a = await (ready ?? initAnalytics());
  if (!a) return;
  try {
    setUserProperties(a, properties);
  } catch {
    // swallow
  }
}

/**
 * Track an SPA page view (GA4 `page_view`). `pageLocation` overrides the URL sent
 * as `page_location` — used for the landing page_view so GA4 sees the original
 * `?utm_*` query (the router strips it before the auto page_view would fire) and
 * attributes the session's source/medium/campaign correctly.
 */
export async function trackPageView(
  path: string,
  title?: string,
  pageLocation?: string,
): Promise<void> {
  return logAnalyticsEvent('page_view', {
    page_path: path,
    page_location:
      pageLocation ?? (typeof window !== 'undefined' ? window.location.href : path),
    page_title: title ?? (typeof document !== 'undefined' ? document.title : undefined),
  });
}
